import { Breadcrumbs, Button, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { Link, useLocation } from 'react-router';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { formatName } from 'src/utils/nameFormatting';

export function AppBreadcrumbs() {
  const location = useLocation();
  const { pathname: path, search } = location;
  const { clientId, client } = useCurrentClientIfAvailable();

  const items = buildBreadcrumbItems(path, search, clientId, client ?? undefined);

  if (items.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs separator={<IconChevronRight size={16} />} mx="sm" mb="md" mt="md">
      {items.map((item, i) => {
        const isCurrent = i === items.length - 1;
        const textProps = isCurrent
          ? {
              c: 'gray.10',
              fw: 700,
            }
          : {
              c: 'gray.9',
              fw: 500,
            };

        if (item.highlight) {
          textProps.c = 'brand.6';
        }

        return (
          <Button component={Link} variant="text" key={item.href} to={item.href}>
            <Text size="md" {...textProps}>
              {item.label}
            </Text>
          </Button>
        );
      })}
    </Breadcrumbs>
  );
}

function buildBreadcrumbItems(
  path: string,
  search: string,
  clientId: string | undefined,
  client: { firstName?: string; lastName?: string } | undefined,
) {
  if (
    path.includes('financial-items/') ||
    path.includes('financial-data/create') ||
    path.includes('file-upload') ||
    path.match(/\/client\/[^/]+\/suitability-report$/) ||
    path.match(/\/client\/[^/]+\/meeting-agenda$/) ||
    path.match(/\/client\/[^/]+\/data-export$/) ||
    path.match(/\/client\/[^/]+\/meeting-note$/) ||
    path.match(/\/client\/[^/]+\/document\/[^/]+\/[^/]+\/edit$/)
  ) {
    return [];
  }

  const parts: { label: string | null; href: string; highlight?: boolean }[] = [];

  if (clientId != null && path.startsWith(`/client/${clientId}`)) {
    const clientName = formatName(client?.firstName, client?.lastName);
    parts.push(
      { label: 'All clients', href: '/clients/all' },
      {
        label: clientName,
        href: `/client/${clientId}/non-financial-data`,
        highlight: true,
      },
    );
  }

  if (search.includes('fileId')) {
    parts.push({
      label: 'Documents',
      href: `/client/${clientId}/document/add`,
    });
  }

  if (search.includes('fileName')) {
    const fileName = new URLSearchParams(search).get('fileName');
    parts.push({ label: fileName, href: window.location.href });
  }

  return parts;
}
